/**
 * useMobileCompanyProfile Hook
 * Enhanced hook specifically for MobileCompanyProfile component
 * Extends base useCompany with mobile-specific features and optimizations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import useCompany from './useCompany';
import useApi from './useApi';
import { API_CONFIG } from '../config/api';
import { normalizeMediaUrl } from '../utils/normalizeMediaUrl';

const useMobileCompanyProfile = (initialCompanyId = null) => {
  // Base company hook functionality
  const baseCompany = useCompany();

  // Company size options mapping (display values in Italian, backend values in English)
  const companySizeOptions = [
    { display: '1-10 dipendenti', value: '1-10' },
    { display: '11-50 dipendenti', value: '11-50' },
    { display: '51-200 dipendenti', value: '51-200' },
    { display: '201-500 dipendenti', value: '201-500' },
    { display: '501-1000 dipendenti', value: '501-1000' },
    { display: '1001-5000 dipendenti', value: '1001-5000' },
    { display: '5000+ dipendenti', value: '5000+' },
    { display: 'Sconosciuto', value: 'Unknown' }
  ];

  // Helper functions to convert between display and backend values
  const getCompanySizeBackendValue = useCallback((displayValue) => {
    const option = companySizeOptions.find(opt => opt.display === displayValue);
    return option ? option.value : displayValue;
  }, []);

  const getCompanySizeDisplayValue = useCallback((backendValue) => {
    const option = companySizeOptions.find(opt => opt.value === backendValue);
    return option ? option.display : backendValue;
  }, []);
  
  // Enhanced mobile-specific states
  const [currentStep, setCurrentStep] = useState(1); // 1: AI Setup, 2: Manual Review
  const [showManualMode, setShowManualMode] = useState(false);
  const [aiAnalysisComplete, setAiAnalysisComplete] = useState(false);
  const [isGeneratingDna, setIsGeneratingDna] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize profileData first
  const initialProfileData = {
    // Company Information (mapped to backend fields)
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    size: '',
    founded_year: '',
    logo: null,

    // Business Information (backend compatible)
    description: '',
    industry: '',
    target_audience: '',
    brand_values: '',
    brand_personality: '', // Maps to brand_voice in backend
    communication_style: '',
    competitive_advantage: '',
    key_products: '',
    company_history: '',
    mission_vision: '',
    unique_selling_points: [],
    competitors: [],

    // DNA and Analysis
    generated_dna: '',
    aiAnalysis: null,

    // Media and Documents
    uploadedDocuments: [],
    uploadedImages: [],
    uploadedLinks: []
  };

  const [profileData, setProfileData] = useState(initialProfileData);
  const [lastSavedState, setLastSavedState] = useState(JSON.parse(JSON.stringify(initialProfileData)));
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // API hook for direct calls
  const api = useApi();
  const initialized = useRef(false);
  const lastCompanyIdRef = useRef(null);

  // Initialize with company ID if provided
  useEffect(() => {
    if (initialCompanyId && baseCompany.companies.length > 0) {
      const company = baseCompany.companies.find(c => c.id === initialCompanyId) || baseCompany.currentCompany;
      if (company) {
        loadCompanyData(company);
        initialized.current = true;
        lastCompanyIdRef.current = company.id;
      }
    }
  }, [initialCompanyId, baseCompany.companies]);

  // Auto-load current company when available or changes
  useEffect(() => {
    const company = baseCompany.currentCompany;
    if (!company) return;

    // Load on first availability or when switching to a different company (if no unsaved changes)
    if (!initialized.current || (lastCompanyIdRef.current !== company.id && !hasUnsavedChanges)) {
      loadCompanyData(company);
      initialized.current = true;
      lastCompanyIdRef.current = company.id;
    }
  }, [baseCompany.currentCompany, hasUnsavedChanges]);

  // Calculate completion percentage
  useEffect(() => {
    const requiredFields = [
      'name', 'description', 'industry', 'target_audience', 
      'brand_values', 'website'
    ];
    const completedFields = requiredFields.filter(field => 
      profileData[field] && profileData[field].toString().trim().length > 0
    );
    
    // Add logo and documents to completion calculation
    const hasLogo = !!profileData.logo;
    const hasDocuments = profileData.uploadedDocuments.length > 0;
    
    const totalRequirements = requiredFields.length + 2; // +2 for logo and documents
    const completedRequirements = completedFields.length + (hasLogo ? 1 : 0) + (hasDocuments ? 1 : 0);
    
    const percentage = Math.round((completedRequirements / totalRequirements) * 100);
    setCompletionPercentage(percentage);
  }, [profileData]);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(profileData) !== JSON.stringify(lastSavedState);
    console.log('[useMobileCompanyProfile] Tracking changes:', {
      hasChanges,
      profileDataKeys: Object.keys(profileData),
      lastSavedStateKeys: Object.keys(lastSavedState)
    });
    setHasUnsavedChanges(hasChanges);
  }, [profileData, lastSavedState]);

  /**
   * Create a snapshot of current state for comparison
   */
  const createStateSnapshot = useCallback((data = profileData) => {
    return JSON.parse(JSON.stringify(data));
  }, [profileData]);

  /**
   * Load company data into profile state with proper field mapping
   */
  const loadCompanyData = useCallback((company) => {
    console.log('[useMobileCompanyProfile] Loading company data:', company.id);

    // Map existing logo
    let logoData = null;
    if (company.logo && Array.isArray(company.logo) && company.logo.length > 0) {
      const existingLogo = company.logo[company.logo.length - 1];
      if (existingLogo.url && existingLogo.size) {
        const normalizedLogoUrl = normalizeMediaUrl(existingLogo.url, API_CONFIG.BASE_URL);
        logoData = {
          id: 'existing-logo',
          name: existingLogo.original_filename || existingLogo.description || 'Company Logo',
          size: `${(existingLogo.size / 1024 / 1024).toFixed(2)} MB`,
          preview: normalizedLogoUrl,
          url: normalizedLogoUrl,
          file: null,
          existing: true
        };
      }
    }

    // Map existing brand images
    let existingImages = [];
    if (company.brand_images && Array.isArray(company.brand_images) && company.brand_images.length > 0) {
      try {
        existingImages = company.brand_images
          .filter(img => img.url && img.size)
          .map((img, index) => ({
            id: `existing-img-${index}`,
            name: img.original_filename || img.description || `Image ${index + 1}`,
            size: `${(img.size / 1024 / 1024).toFixed(2)} MB`,
            preview: normalizeMediaUrl(img.url, API_CONFIG.BASE_URL),
            file: null,
            existing: true,
            url: normalizeMediaUrl(img.url, API_CONFIG.BASE_URL)
          }));
      } catch (error) {
        console.error('[useMobileCompanyProfile] Error loading existing images:', error);
      }
    }

    // Map existing documents
    let existingDocs = [];
    if (company.documents && Array.isArray(company.documents) && company.documents.length > 0) {
      try {
        existingDocs = company.documents
          .filter(doc => doc.url && doc.size)
          .map((doc, index) => ({
            id: `existing-doc-${index}`,
            name: doc.original_filename || doc.description || `Document ${index + 1}`,
            size: `${(doc.size / 1024 / 1024).toFixed(2)} MB`,
            type: doc.file_type,
            file: null,
            existing: true,
            url: normalizeMediaUrl(doc.url, API_CONFIG.BASE_URL)
          }));
      } catch (error) {
        console.error('[useMobileCompanyProfile] Error loading existing documents:', error);
      }
    }

    // Map existing other_links
    let existingLinks = [];
    if (company.other_links && Array.isArray(company.other_links) && company.other_links.length > 0) {
      try {
        existingLinks = company.other_links
          .filter(link => link.url && (link.description || link.title))
          .map((link, index) => ({
            id: `existing-link-${index}`,
            description: link.description || link.title || `Link ${index + 1}`, // Use description field
            url: link.url,
            existing: true
          }));
      } catch (error) {
        console.error('[useMobileCompanyProfile] Error loading existing links:', error);
      }
    }

    // Build the new profile data object
    const newProfileData = {
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.headquarters || company.address || '',
      website: company.website || '',
      size: getCompanySizeDisplayValue(company.company_size || ''), // Convert backend value to display value
      founded_year: company.founded_year || '',
      description: company.description || '',
      industry: company.industry || '',
      target_audience: company.target_audience || '',
      brand_values: company.brand_values || (Array.isArray(company.values) ? company.values.join(', ') : ''),
      brand_personality: company.brand_personality || company.brand_voice || '', // FIXED: check brand_personality first
      communication_style: company.communication_style || '',
      competitive_advantage: company.competitive_advantage || '',
      key_products: company.key_products_services || company.key_products || '', // FIXED: check key_products_services first
      company_history: company.company_history || '',
      mission_vision: company.mission_vision || '',
      unique_selling_points: company.unique_selling_points || [],
      competitors: company.competitors || [],
      aiAnalysis: company.ai_analysis || null,
      generated_dna: company.generated_dna || (company.dna ? (typeof company.dna === 'string' ? company.dna : company.dna.dna || JSON.stringify(company.dna, null, 2)) : '') || company.ai_analysis?.company_identity || '',
      // Media and links populated from backend
      logo: logoData,
      uploadedImages: existingImages,
      uploadedDocuments: existingDocs,
      uploadedLinks: existingLinks
    };

    setProfileData(newProfileData);

    // Set as saved state - create snapshot of the profileData we just built
    const profileSnapshot = JSON.parse(JSON.stringify(newProfileData));
    setLastSavedState(profileSnapshot);
    setHasUnsavedChanges(false);
    lastCompanyIdRef.current = company.id;

    console.log('[useMobileCompanyProfile] Company data loaded, saved state set:', {
      companyId: company.id,
      hasData: !!newProfileData.name
    });
  }, [createStateSnapshot, getCompanySizeDisplayValue]);

  /**
   * Enhanced AI Analysis with real-time progress tracking (NEW IMPLEMENTATION)
   */
  const performAiAnalysis = useCallback(async (websiteUrl, uploadedDocs = [], uploadedImages = []) => {
    console.log('[useMobileCompanyProfile] Starting enhanced AI analysis');

    const hasWebsite = websiteUrl && websiteUrl.trim() !== '';
    const hasDocuments = uploadedDocs.length > 0;
    const hasImages = uploadedImages.length > 0;

    if (!hasWebsite && !hasDocuments && !hasImages) {
      throw new Error('Fornisci almeno un sito web o carica dei documenti per l\'analisi AI.');
    }

    if (!baseCompany.currentCompany) {
      throw new Error('Nessuna azienda selezionata. Crea prima un\'azienda.');
    }

    try {
      baseCompany.clearError();
      const companyId = baseCompany.currentCompany.id;

      console.log('[useMobileCompanyProfile] AI Analysis: Starting new analysis flow...');

      // STEP 1: Save changes to backend before analysis (if website changed)
      const textData = {};
      const fileData = {};

      // Check if website changed
      const currentWebsite = websiteUrl?.trim() || '';
      const originalWebsite = baseCompany.currentCompany.website?.trim() || '';
      if (currentWebsite !== originalWebsite && currentWebsite) {
        textData.website = websiteUrl;
      }

      // Check for new documents
      const newDocuments = uploadedDocs?.filter(doc => doc.file) || [];
      if (newDocuments.length > 0) {
        fileData.documents = newDocuments.map(doc => ({
          file: doc.file,
          description: 'Company document',
          tags: ['company', 'documents', 'ai_analysis'],
          usage_context: ['ai_analysis', 'company_profile']
        }));
      }

      // Check for new images
      const newImages = uploadedImages?.filter(img => img.file) || [];
      if (newImages.length > 0) {
        fileData.brand_images = newImages.map(img => ({
          file: img.file,
          description: 'Brand image',
          tags: ['brand', 'images', 'marketing', 'ai_analysis'],
          usage_context: ['ai_analysis', 'company_profile']
        }));
      }

      // Save changes if there are any
      const hasTextChanges = Object.keys(textData).length > 0;
      const hasFileChanges = Object.keys(fileData).length > 0;
      const hasChanges = hasTextChanges || hasFileChanges;

      if (hasChanges) {
        console.log('[useMobileCompanyProfile] AI Analysis: Saving changes...', { textData, fileData });

        if (hasFileChanges) {
          await baseCompany.updateCompanyWithFiles(companyId, textData, fileData);
        } else {
          await baseCompany.updateCompany(companyId, textData);
        }

        console.log('[useMobileCompanyProfile] AI Analysis: Changes saved successfully');
      }

      // STEP 2: Start AI Analysis with new endpoint
      const analysisOptions = {
        analysis_type: 'standard',
        include_website: hasWebsite,
        include_documents: hasDocuments,
        include_images: hasImages,
        user_instructions: 'Analizza tutti i materiali disponibili per creare un DNA aziendale completo'
      };

      console.log('[useMobileCompanyProfile] Starting AI analysis with options:', analysisOptions);

      // Start AI analysis using the new endpoint
      const analysisResponse = await baseCompany.analyzeCompanyMaterials(companyId, analysisOptions);

      console.log('[useMobileCompanyProfile] Analysis response received:', analysisResponse);

      // DNA generation is synchronous: use the returned DNA string and do not overwrite profile fields.
      const dnaText =
        (typeof analysisResponse?.dna === 'string' && analysisResponse.dna) ||
        (typeof analysisResponse?.company?.dna === 'string' && analysisResponse.company.dna) ||
        '';

      if (!dnaText) {
        console.error('[useMobileCompanyProfile] Empty DNA in response:', analysisResponse);
        throw new Error('DNA generation returned an empty result');
      }

      setProfileData((prev) => ({ ...prev, generated_dna: dnaText }));
      setAiAnalysisComplete(true);
      setCurrentStep(2);
      return analysisResponse;

    } catch (error) {
      console.error('[useMobileCompanyProfile] AI analysis failed:', error);
      throw error;
    }
  }, [profileData, baseCompany]);

  /**
   * Generate DNA text from analysis results
   */
  const generateDnaFromAnalysis = useCallback((company, analysis) => {
    if (!company && !analysis) return '';
    
    const companyName = company?.name || profileData.name || 'La tua Azienda';
    const description = company?.description || profileData.description || '';
    
    if (analysis?.analysis) {
      const analysisData = analysis.analysis;
      return `DNA Aziendale per ${companyName}

${description}

IDENTITÀ DEL BRAND:
${analysisData.company_identity || 'Identità aziendale in fase di definizione basata sui materiali forniti.'}

PROFILO BUSINESS:
${analysisData.business_profile || 'Profilo business in elaborazione tramite analisi AI.'}

STILE COMUNICATIVO:
${analysisData.communication_style || 'Stile di comunicazione in fase di analisi.'}

TARGET AUDIENCE:
${analysisData.target_audience_insights || 'Insights sul target audience in fase di raccolta.'}

STRATEGIA CONTENUTI:
${analysisData.content_strategy || 'Strategia contenuti in fase di sviluppo basata sull\'analisi aziendale.'}

Questo DNA guida la creazione di contenuti autentici e coerenti con l'identità aziendale.`;
    }
    
    // Fallback DNA generation
    return `DNA Aziendale per ${companyName}

${description}

Analisi AI completata. Il DNA aziendale è stato generato basandosi sui materiali forniti e include:
- Identità e valori del brand
- Profilo business e posizionamento
- Stile comunicativo preferito
- Strategia contenuti personalizzata

Questo profilo sarà utilizzato per guidare la creazione di contenuti marketing coerenti.`;
  }, [profileData]);

  /**
   * Extract company name from website URL
   */
  const extractCompanyNameFromUrl = useCallback((url) => {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      const name = domain.split('.')[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      return '';
    }
  }, []);

  /**
   * Generate fallback DNA when AI is not available
   */
  const generateFallbackDna = useCallback((customPrompt = '') => {
    const companyName = profileData.name || 'La tua Azienda';
    const industry = profileData.industry || 'settore innovativo';
    const values = profileData.brand_values || 'valori distintivi';
    const audience = profileData.target_audience || 'clienti target';
    const voice = profileData.brand_personality || 'comunicazione autentica';

    return `DNA Aziendale per ${companyName}

${profileData.description || 'Una realtà dinamica e innovativa nel proprio settore.'}

IDENTITÀ DEL BRAND:
${companyName} opera nel settore ${industry.toLowerCase()} con un forte orientamento verso ${values.toLowerCase()}. La personalità del brand riflette ${voice.toLowerCase()} che si traduce in ogni interazione con ${audience.toLowerCase()}.

MISSION E POSIZIONAMENTO:
L'azienda si distingue per la sua capacità di offrire soluzioni innovative mantenendo un approccio ${voice.toLowerCase()}. Il focus principale è rivolto a ${audience.toLowerCase()} attraverso una comunicazione diretta ed efficace.

STRATEGIA COMUNICATIVA:
La comunicazione di ${companyName} segue i principi di ${values.toLowerCase()}, utilizzando un tono ${voice.toLowerCase()} per raggiungere ${audience.toLowerCase()}.

${customPrompt ? `PERSONALIZZAZIONE RICHIESTA:\n${customPrompt}\n` : ''}

Questo DNA aziendale deve essere il punto di riferimento per tutte le future iniziative di marketing e comunicazione, garantendo coerenza e autenticità in ogni touchpoint con il cliente.`;
  }, [profileData]);

  /**
   * Generate AI-powered DNA content using the correct API endpoint
   */
  const generateAiDna = useCallback(async (customPrompt = '') => {
    console.log('[useMobileCompanyProfile] Generating AI DNA with custom prompt');

    setIsGeneratingDna(true);

    try {
      // Use the correct company ID
      const companyId = baseCompany.currentCompany?.id;
      if (!companyId) {
        throw new Error('No company selected for DNA generation');
      }

      // Use the existing analyze endpoint with custom prompt
      const analysisData = {
        include_website: !!profileData.website,
        analysis_type: 'dna_generation',
        custom_prompt: customPrompt,
        company_context: {
          name: profileData.name,
          description: profileData.description,
          industry: profileData.industry,
          target_audience: profileData.target_audience,
          brand_values: profileData.brand_values,
          brand_voice: profileData.brand_personality,
          website: profileData.website
        }
      };

      // Use the existing analyze endpoint for DNA generation
      const response = await baseCompany.analyzeCompanyMaterials(companyId, analysisData);

      if (response && response.analysis) {
        const analysisResult = response.analysis;
        const generatedDnaText = analysisResult.company_identity ||
                                 analysisResult.business_profile ||
                                 generateDnaFromAnalysis(null, response);

        setProfileData(prev => ({
          ...prev,
          generated_dna: generatedDnaText,
          aiAnalysis: analysisResult
        }));

        console.log('[useMobileCompanyProfile] Company DNA generated successfully');
        return generatedDnaText;
      }

      throw new Error('No DNA content generated from analysis');

    } catch (error) {
      console.error('[useMobileCompanyProfile] AI DNA generation failed:', error);

      // Fallback: generate DNA from current profile data
      const fallbackDna = generateFallbackDna(customPrompt);
      setProfileData(prev => ({
        ...prev,
        generated_dna: fallbackDna
      }));

      return fallbackDna;
    } finally {
      setIsGeneratingDna(false);
    }
  }, [profileData, baseCompany, generateDnaFromAnalysis, generateFallbackDna]);


  /**
   * Save profile data to backend
   */
  const saveProfile = useCallback(async () => {
    console.log('[useMobileCompanyProfile] Saving profile data');

    try {
      // Try to get companyId from currentCompany or from the first company in the list
      let companyId = baseCompany.currentCompany?.id;

      // If no current company but we have companies, use the first one
      if (!companyId && baseCompany.companies && baseCompany.companies.length > 0) {
        companyId = baseCompany.companies[0].id;
      }

      let savedCompany;

      if (companyId) {
        // Update existing company with proper field mapping
        const updateData = {};

        // Only add fields that actually CHANGED (compare with current saved values)
        const addFieldIfChanged = (fieldKey, newValue, backendKey = null) => {
          const key = backendKey || fieldKey;
          const currentValue = baseCompany.currentCompany?.[key];

          // Normalize for comparison (trim strings, handle null/undefined)
          const normalizedNew = newValue ? String(newValue).trim() : '';
          const normalizedCurrent = currentValue ? String(currentValue).trim() : '';

          // Only add if value changed and new value is not empty
          if (normalizedNew && normalizedNew !== normalizedCurrent) {
            updateData[key] = newValue;
          }
        };

        addFieldIfChanged('name', profileData.name);
        addFieldIfChanged('description', profileData.description);
        addFieldIfChanged('industry', profileData.industry);
        addFieldIfChanged('website', profileData.website);
        addFieldIfChanged('email', profileData.email);
        addFieldIfChanged('phone', profileData.phone);
        addFieldIfChanged('address', profileData.address, 'headquarters');

        // Convert display value to backend value for company_size and check if changed
        if (profileData.size) {
          const backendSize = getCompanySizeBackendValue(profileData.size);
          const currentSize = baseCompany.currentCompany?.company_size;
          if (backendSize && backendSize !== currentSize) {
            updateData.company_size = backendSize;
          }
        }

        addFieldIfChanged('target_audience', profileData.target_audience);
        addFieldIfChanged('brand_personality', profileData.brand_personality);
        addFieldIfChanged('brand_values', profileData.brand_values);
        addFieldIfChanged('communication_style', profileData.communication_style);
        addFieldIfChanged('competitive_advantage', profileData.competitive_advantage);
        addFieldIfChanged('key_products', profileData.key_products, 'key_products_services');
        addFieldIfChanged('company_history', profileData.company_history);
        addFieldIfChanged('mission_vision', profileData.mission_vision);
        addFieldIfChanged('generated_dna', profileData.generated_dna);

        // Handle founded_year separately (convert to int and check if changed)
        if (profileData.founded_year) {
          const yearInt = parseInt(profileData.founded_year);
          const currentYear = baseCompany.currentCompany?.founded_year;
          if (!isNaN(yearInt) && yearInt !== currentYear) {
            updateData.founded_year = yearInt;
          }
        }

        // Handle arrays - only add if not empty
        if (Array.isArray(profileData.unique_selling_points) && profileData.unique_selling_points.length > 0) {
          updateData.unique_selling_points = profileData.unique_selling_points;
        }

        if (Array.isArray(profileData.competitors) && profileData.competitors.length > 0) {
          updateData.competitors = profileData.competitors;
        }

        // Handle links - use description instead of title
        if (Array.isArray(profileData.uploadedLinks) && profileData.uploadedLinks.length > 0) {
          const validLinks = profileData.uploadedLinks
            .map(l => ({
              url: l.url,
              description: l.description || l.title || l.url // Fallback to title or url if no description
            }))
            .filter(l => !!l.url && !!l.description); // Ensure both url and description exist

          if (validLinks.length > 0) {
            updateData.other_links = validLinks;
            updateData.useful_links = validLinks.map(l => l.url);
          }
        }
        
        // Prepare file data - ONLY include new files (with .file property)
        const fileData = {};

        // Check if logo changed (new upload or removal)
        const currentLogoExists = profileData.logo !== null;
        const originalLogoExists = baseCompany.currentCompany?.logo && baseCompany.currentCompany.logo.length > 0;
        const logoChanged = currentLogoExists !== originalLogoExists || (profileData.logo && profileData.logo.file);

        if (logoChanged) {
          // If we have a new logo file to upload
          if (profileData.logo && profileData.logo.file && profileData.logo.file instanceof File) {
            fileData.logo = [{
              file: profileData.logo.file,
              description: 'Company logo',
              tags: ['logo', 'brand', 'primary'],
              usage_context: ['website', 'marketing', 'documents']
            }];
          } else if (!profileData.logo) {
            // Logo was removed - send empty array
            updateData.logo = [];
          }
        }

        // Check if brand images changed (new uploads or removals)
        const currentImageCount = profileData.uploadedImages.length;
        const originalImageCount = baseCompany.currentCompany?.brand_images?.length || 0;
        const newImages = profileData.uploadedImages.filter(img => img.file && img.file instanceof File);
        const imagesChanged = currentImageCount !== originalImageCount || newImages.length > 0;

        if (imagesChanged) {
          // If we have new images to upload, add them to fileData
          if (newImages.length > 0) {
            // Validate each file before adding
            const validImages = newImages.filter(img => img.file instanceof File && img.file.size > 0);

            if (validImages.length > 0) {
              fileData.brand_images = validImages.map((img, index) => ({
                file: img.file,
                description: img.name || `Brand image ${index + 1}`,
                tags: ['brand', 'marketing'],
                usage_context: ['social_media', 'website']
              }));
            }
          }

          // Send existing images to keep in textData
          const existingImages = profileData.uploadedImages.filter(img => img.existing && img.url);
          if (existingImages.length > 0) {
            updateData.brand_images = existingImages.map(img => ({
              filename: img.name,
              original_filename: img.name,
              url: img.url,
              size: parseInt(String(img.size).replace(/[^\d]/g, '')) || 0,
              file_type: 'image',
              description: img.description || img.name,
              tags: img.tags || [],
              usage_context: img.usage_context || []
            }));
          } else if (newImages.length === 0) {
            // No existing images and no new images = remove all
            updateData.brand_images = [];
          }
        }

        // Check if documents changed (new uploads or removals)
        const currentDocCount = profileData.uploadedDocuments.length;
        const originalDocCount = baseCompany.currentCompany?.documents?.length || 0;
        const newDocs = profileData.uploadedDocuments.filter(doc => doc.file && doc.file instanceof File);
        const docsChanged = currentDocCount !== originalDocCount || newDocs.length > 0;

        if (docsChanged) {
          // If we have new documents to upload, add them to fileData
          if (newDocs.length > 0) {
            fileData.documents = newDocs.map(doc => ({
              file: doc.file,
              description: doc.name || 'Company document',
              tags: ['company_profile', 'documentation'],
              usage_context: ['ai_analysis', 'reference']
            }));
          }

          // Send existing documents to keep in textData
          const existingDocs = profileData.uploadedDocuments.filter(doc => doc.existing && doc.url);
          if (existingDocs.length > 0) {
            updateData.documents = existingDocs.map(doc => ({
              filename: doc.name,
              original_filename: doc.name,
              url: doc.url,
              size: parseInt(String(doc.size).replace(/[^\d]/g, '')) || 0,
              file_type: doc.type || 'document',
              description: doc.description || doc.name,
              tags: doc.tags || [],
              usage_context: doc.usage_context || []
            }));
          } else if (newDocs.length === 0) {
            // No existing documents and no new documents = remove all
            updateData.documents = [];
          }
        }

        // Use updateCompanyWithFiles only if there are files to upload
        const hasFiles = Object.keys(fileData).length > 0;

        let savedCompany;
        if (hasFiles) {
          // Check if we have text changes or only file changes
          const hasTextChanges = Object.keys(updateData).length > 0;
          const hasOnlyFileChanges = !hasTextChanges && Object.keys(fileData).length > 0;

          if (hasOnlyFileChanges) {
            // OPTIMIZATION: If only files changed (no text changes), send ONLY files
            savedCompany = await baseCompany.updateCompanyWithFiles(companyId, {}, fileData);
          } else {
            // Send both text and files
            savedCompany = await baseCompany.updateCompanyWithFiles(companyId, updateData, fileData);
          }
        } else {
          savedCompany = await baseCompany.updateCompany(companyId, updateData);
        }
      } else {
        // Cannot create or update company without a companyId
        console.error('[useMobileCompanyProfile] SAVE: Cannot save - no company selected');
        throw new Error('Nessuna azienda selezionata. Seleziona un\'azienda esistente per caricare file.');
      }
      
      // Update saved state
      setLastSavedState(createStateSnapshot(profileData));
      setHasUnsavedChanges(false);
      
      console.log('[useMobileCompanyProfile] Profile saved successfully');
      return savedCompany;

    } catch (error) {
      console.error('[useMobileCompanyProfile] Failed to save profile:', error);
      throw error;
    }
  }, [profileData, baseCompany, createStateSnapshot, getCompanySizeBackendValue]);

  /**
   * Update profile field
   */
  const updateProfileField = useCallback((field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Add uploaded document
   */
  const addUploadedDocument = useCallback((document) => {
    setProfileData(prev => ({
      ...prev,
      uploadedDocuments: [...prev.uploadedDocuments, document]
    }));
  }, []);

  /**
   * Remove uploaded document
   */
  const removeUploadedDocument = useCallback((documentId) => {
    setProfileData(prev => ({
      ...prev,
      uploadedDocuments: prev.uploadedDocuments.filter(doc => doc.id !== documentId)
    }));
  }, []);

  /**
   * Add uploaded image
   */
  const addUploadedImage = useCallback((image) => {
    setProfileData(prev => ({
      ...prev,
      uploadedImages: [...prev.uploadedImages, image]
    }));
  }, []);

  /**
   * Remove uploaded image
   */
  const removeUploadedImage = useCallback((imageId) => {
    setProfileData(prev => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter(img => img.id !== imageId)
    }));
  }, []);

  /**
   * Add link
   */
  const addLink = useCallback((link) => {
    setProfileData(prev => ({
      ...prev,
      uploadedLinks: [...prev.uploadedLinks, link]
    }));
  }, []);

  /**
   * Remove link
   */
  const removeLink = useCallback((linkId) => {
    setProfileData(prev => ({
      ...prev,
      uploadedLinks: prev.uploadedLinks.filter(link => link.id !== linkId)
    }));
  }, []);

  /**
   * Reset to last saved state
   */
  const resetToLastSaved = useCallback(() => {
    if (lastSavedState) {
      setProfileData(lastSavedState);
      setHasUnsavedChanges(false);
    }
  }, [lastSavedState]);

  /**
   * Check if unsaved changes exist before action
   */
  const checkUnsavedChanges = useCallback((action) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Hai modifiche non salvate. Vuoi continuare comunque?'
      );
      if (confirmed) {
        action();
      }
      return confirmed;
    } else {
      action();
      return true;
    }
  }, [hasUnsavedChanges]);

  /**
   * Handle file upload for different types (documents, images, logo)
   */
  const handleFileUpload = useCallback(async (files, type) => {
    console.log(`[useMobileCompanyProfile] Handling ${type} upload:`, files);

    try {
      const fileArray = Array.from(files);

      // File size limits (from API documentation)
      const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB for images
      const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB for documents

      if (type === 'logo') {
        // Handle logo upload - single file
        const logoFile = fileArray[0];
        if (logoFile) {
          // Validate file size
          if (logoFile.size > MAX_IMAGE_SIZE) {
            throw new Error(`Logo troppo grande. Dimensione massima: 5MB`);
          }

          // Validate file type
          const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
          if (!allowedTypes.includes(logoFile.type)) {
            throw new Error(`Tipo di file non supportato. Usa: JPG, PNG, GIF, WEBP`);
          }

          const logoData = {
            id: Date.now(),
            name: logoFile.name,
            file: logoFile,
            preview: URL.createObjectURL(logoFile),
            size: `${Math.round(logoFile.size / 1024)}KB`
          };

          setProfileData(prev => ({
            ...prev,
            logo: logoData
          }));
        }

      } else if (type === 'documents') {
        // Handle document upload - multiple files
        // Validate each file
        const validFiles = [];
        for (const file of fileArray) {
          if (file.size > MAX_DOCUMENT_SIZE) {
            console.warn(`[useMobileCompanyProfile] Document ${file.name} too large, skipping`);
            alert(`Documento "${file.name}" troppo grande (max 10MB). Saltato.`);
            continue;
          }
          validFiles.push(file);
        }

        const documentData = validFiles.map(file => ({
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          size: `${Math.round(file.size / 1024)}KB`,
          uploadDate: new Date().toLocaleDateString()
        }));

        setProfileData(prev => ({
          ...prev,
          uploadedDocuments: [...prev.uploadedDocuments, ...documentData]
        }));

      } else if (type === 'images') {
        // Handle image upload - multiple files
        // Validate each file
        const validFiles = [];
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];

        for (const file of fileArray) {
          if (file.size > MAX_IMAGE_SIZE) {
            console.warn(`[useMobileCompanyProfile] Image ${file.name} too large, skipping`);
            alert(`Immagine "${file.name}" troppo grande (max 5MB). Saltata.`);
            continue;
          }

          if (!allowedTypes.includes(file.type)) {
            console.warn(`[useMobileCompanyProfile] Image ${file.name} invalid type, skipping`);
            alert(`Immagine "${file.name}" tipo non supportato. Usa JPG, PNG, GIF, WEBP. Saltata.`);
            continue;
          }

          validFiles.push(file);
        }

        const imageData = validFiles.map(file => ({
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          preview: URL.createObjectURL(file),
          size: `${Math.round(file.size / 1024)}KB`,
          uploadDate: new Date().toLocaleDateString()
        }));

        setProfileData(prev => ({
          ...prev,
          uploadedImages: [...prev.uploadedImages, ...imageData]
        }));
      }

      console.log(`[useMobileCompanyProfile] ${type} files added successfully`);

    } catch (error) {
      console.error(`[useMobileCompanyProfile] Error handling ${type} upload:`, error);
      throw error;
    }
  }, []);

  /**
   * Remove file of any type
   */
  const removeFile = useCallback((type, fileId) => {
    console.log(`[useMobileCompanyProfile] Removing ${type}:`, fileId);
    
    if (type === 'logo') {
      setProfileData(prev => ({
        ...prev,
        logo: null
      }));
    } else if (type === 'documents') {
      removeUploadedDocument(fileId);
    } else if (type === 'images') {
      removeUploadedImage(fileId);
    } else if (type === 'links') {
      removeLink(fileId);
    }
  }, [removeUploadedDocument, removeUploadedImage, removeLink]);

  return {
    // Inherited from base hook
    ...baseCompany,
    
    // Mobile-specific state
    currentStep,
    setCurrentStep,
    showManualMode,
    setShowManualMode,
    aiAnalysisComplete,
    setAiAnalysisComplete,
    isGeneratingDna,
    hasUnsavedChanges,
    completionPercentage,
    profileData,
    
    // Enhanced actions
    loadCompanyData,
    performAiAnalysis,
    generateAiDna,
    saveProfile,
    updateProfileField,
    addUploadedDocument,
    removeUploadedDocument,
    addUploadedImage,
    removeUploadedImage,
    addLink,
    removeLink,
    resetToLastSaved,
    checkUnsavedChanges,
    handleFileUpload,
    removeFile,
    
    // Utilities
    generateDnaFromAnalysis,
    extractCompanyNameFromUrl,
    createStateSnapshot,
    
    // Combined loading states
    isMobileProcessing: baseCompany.loading || baseCompany.analyzing || baseCompany.uploading || isGeneratingDna,
    
    // Validation helpers
    isProfileValid: completionPercentage >= 70,
    canProceedToStep2: (profileData.website || profileData.uploadedDocuments.length > 0),
    
    // Data transformation helpers
    getCompanyDataForApi: () => ({
      name: profileData.name,
      description: profileData.description,
      industry: profileData.industry,
      website: profileData.website,
      email: profileData.email,
      phone: profileData.phone,
      headquarters: profileData.address,
      company_size: profileData.size,
      founded_year: profileData.founded_year ? parseInt(profileData.founded_year) : null,
      target_audience: profileData.target_audience,
      brand_voice: profileData.brand_personality,
      values: profileData.brand_values.split(',').map(v => v.trim()).filter(v => v),
      unique_selling_points: profileData.unique_selling_points,
      competitors: profileData.competitors
    })
  };
};

export default useMobileCompanyProfile;
